//////////////////
// IMPORTS
//////////////////
// libraries
import * as ISchema from "@schema";
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
// event-coloring-settings imports
import { IContainer, IProps, IState } from "./EventColoringSettings.d";
import { eventColoringSettingsFragments } from "./EventColoringSettings.gql";
import * as s from "./EventColoringSettings.styles";
// other components
import { EventCategoryColoring } from "@components/event-category-coloring";
import { A } from "@ui";
// util
import { logger } from "@util/logger.util";
import {
  filterAndSortCategories,
  resetEventColoringSettings,
} from "@util/event-category-coloring.util";
import { articleUrls } from "@clockwise/client-commons/src/constants/help-center";
import { track, TrackingEvents } from "@util/analytics.util";
// state
import { IReduxState } from "@reducers/root.reducer";
import { updateToggleEventColoringSettings } from "@mutations";
import { snackBarOpen } from "@actions/snack-bar.actions";
// material-ui
import { FormControl, Switch } from "@material-ui/core";

//////////////////
// COMPONENT
//////////////////
class EventColoringSettingsCmpt extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      toggled: this.getToggleState(props),
    };
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    const toggled = this.getToggleState(nextProps);
    this.setState({ toggled });
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private getToggleState = (props: IProps) => {
    const { org } = props;
    const eventCategoryColorings = filterAndSortCategories(org.eventColoringSettingsErrorable);
    return eventCategoryColorings.some((category) => category.active);
  };

  // ~-~-~-~-~-~-~-
  // Callbacks
  // ~-~-~-~-~-~-~-
  private onLearnMore = () => {
    track(TrackingEvents.HELP.EVENT_COLORING);
  };

  private onError = (error?: Error) => {
    logger.error("updateEventColoringSettings failed", error);
    this.props.snackBarOpen("Color selection failed to save. Please try again.");
  };

  private onSuccess = () => {
    this.props.snackBarOpen("Saved. Your event colors will start updating shortly.");
  };

  private onToggleEventColoring = (
    _event: React.ChangeEvent<HTMLInputElement>,
    toggle: boolean,
  ) => {
    const { org, relay } = this.props;
    const eventCategoryColorings = toggle
      ? resetEventColoringSettings(org.eventColoringSettingsErrorable)
      : filterAndSortCategories(org.eventColoringSettingsErrorable).map((ecc) => ({
          eventCategory: ecc.eventCategory,
          active: ecc.active,
          googleColorKey: ecc.googleColorKey ? ecc.googleColorKey : undefined,
          isUserSet: ecc.isUserSet,
        }));

    // input for mutation
    const input = {
      eventCategoryColorings,
      orgRelayId: org.id,
      enabled: toggle,
    };

    // fire off the mutation
    updateToggleEventColoringSettings(
      relay.environment,
      input,
      () => this.onSuccess(),
      (e) => this.onError(e),
    );
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  public render() {
    const { org, isWebSettings } = this.props;
    const { toggled } = this.state;
    const eventCategoryColorings: ISchema.IEventCategoryColoring[] = filterAndSortCategories(
      org.eventColoringSettingsErrorable,
    );

    return (
      <div style={s.container}>
        <div style={s.preamble}>
          {!isWebSettings ? (
            <div style={s.intro}>
              See your priorities at a glance with automatic meeting categorization and color-coding
              from Clockwise. Use the default colors or configure them to your personal
              preferences.&nbsp;
              <A href={articleUrls.colors} target="_blank" onClick={this.onLearnMore}>
                Learn more
              </A>
              .
            </div>
          ) : (
            ""
          )}
          <div style={s.row}>
            <div style={s.toggle}>
              <FormControl>
                <Switch color="primary" checked={toggled} onChange={this.onToggleEventColoring} />
              </FormControl>
            </div>
            <div style={s.primaryText}>Enable automatic meeting color-coding</div>
          </div>
        </div>
        <div style={toggled ? {} : { cursor: "not-allowed" }}>
          <div style={toggled ? {} : s.disabled}>
            {eventCategoryColorings.map((ecc, i) => (
              <EventCategoryColoring eventCategoryColoring={ecc} org={org} key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }
}

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {};
}

const EventColoringSettingsRedux = connect(mapStateToProps, {
  snackBarOpen,
})(EventColoringSettingsCmpt);

//////////////////
// RELAY
//////////////////
export const EventColoringSettings = createFragmentContainer<IContainer>(
  EventColoringSettingsRedux,
  eventColoringSettingsFragments,
);
